import Constants from 'expo-constants';

/**
 * Server-side Google API proxy (Supabase Edge Function `google-proxy`).
 *
 * App ships with the publishable/anon key + project URL in `app.json` →
 * `extra.supabase`. The Google API key itself lives only on Supabase as
 * the `GOOGLE_API_KEY` function secret — it's never on-device.
 *
 * Each call site (Vision, Places, Static Maps) tries the proxy first; the
 * user can still set a personal Google key in Settings → Admin → API keys
 * to bypass the proxy if they want.
 */

type Extra = {
  supabase?: {
    url?: string;
    anonKey?: string;
  };
};

const extra = (Constants.expoConfig?.extra ?? {}) as Extra;

export const SUPABASE_URL = extra.supabase?.url ?? '';
export const SUPABASE_ANON_KEY = extra.supabase?.anonKey ?? '';

export function isProxyConfigured(): boolean {
  return SUPABASE_URL.length > 0 && SUPABASE_ANON_KEY.length > 0;
}

export function proxyEndpoint(path = ''): string {
  const base = SUPABASE_URL.replace(/\/$/, '');
  return `${base}/functions/v1/google-proxy${path}`;
}

/**
 * Build a URL for any Supabase Edge Function in this project. The
 * existing `proxyEndpoint` helper is hard-coded to the google-proxy
 * function; this one's the generic version so callers like the
 * contact form can reach `contact-form`, future endpoints, etc.,
 * without each one duplicating the URL math.
 */
export function functionEndpoint(name: string, path = ''): string {
  const base = SUPABASE_URL.replace(/\/$/, '');
  return `${base}/functions/v1/${name}${path}`;
}

export function proxyHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    'apikey': SUPABASE_ANON_KEY,
  };
}

export interface ProxyOk<T> { ok: true; payload: T }
export interface ProxyErr {
  ok: false;
  error: { kind: string; message: string };
}
export type ProxyResult<T> = ProxyOk<T> | ProxyErr;

export async function callProxy<T>(body: Record<string, unknown>): Promise<ProxyResult<T>> {
  if (!isProxyConfigured()) {
    return { ok: false, error: { kind: 'no-proxy', message: 'Server proxy not configured.' } };
  }
  try {
    const res = await fetch(proxyEndpoint(), {
      method: 'POST',
      headers: proxyHeaders(),
      body: JSON.stringify(body),
    });
    const payload = await res.json();
    if (!res.ok || payload?.ok === false) {
      const errInfo = payload?.error ?? { kind: 'upstream', message: `HTTP ${res.status}` };
      return { ok: false, error: errInfo };
    }
    return payload as ProxyOk<T>;
  } catch (e) {
    return { ok: false, error: { kind: 'network', message: String(e) } };
  }
}
